/**
 * Карточка одного отзыва блока «Отзывы» — слово владельца 21.10.2026 (ветка
 * stylepack/testimonial-look: повторять чужую воронку в точности, классом).
 *
 * Вынесена из ветки `testimonials` общего рендерера
 * (`PublicBlockRendererCommon.tsx`), потому что у неё своё состояние: фото,
 * которое не загрузилось, убирается, и карточка остаётся без фото — хук нельзя
 * звать в цикле ветки.
 *
 * Расклады фото (`testimonialLook.ts`):
 *  - 'left'   — фото слева во всю высоту карточки (~35 %, cover), справа
 *               цитата и строка «имя … отметка»; карточка обрезает фото по
 *               своему скруглению;
 *  - 'top'    — фото сверху во всю ширину (160 px, cover);
 *  - 'avatar' — круг 40 px слева от имени (нет поля — этот).
 * Нет фото (или не загрузилось) — ровно прежняя разметка карточки.
 */
import React, { useState, type CSSProperties } from 'react';
import { RichTextContent } from './RichTextContent';
import { asRichField } from '@/types/rich-text';
import type { VariableContext } from '@/player/variable-context';
import type { TestimonialItem } from '@/store/editorSlice';
import { resolveBlockBorder, type BlockBorderFields } from './blockBorder';
import { RATING_STAR_COLOR } from './ratingStarColor';
import type { BlockTextRoles } from './semanticTextRoles';
import {
  TESTIMONIAL_AVATAR_SIZE,
  TESTIMONIAL_LEFT_PHOTO_WIDTH,
  TESTIMONIAL_TOP_PHOTO_HEIGHT,
  type TestimonialLook,
  testimonialBadgeText,
  testimonialPhotoSrc,
} from './testimonialLook';

const CARD_PADDING = '16px 20px';

/** Значок отметки: щит с галочкой цветом отметки (currentColor), галочка — прорезью. */
function ShieldCheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M12 2 4 5v6c0 5.2 3.4 9.7 8 11 4.6-1.3 8-5.8 8-11V5l-8-3Zm-1.2 13.6 5.7-5.7-1.4-1.4-4.3 4.3-2.1-2.1-1.4 1.4 3.5 3.5Z"
      />
    </svg>
  );
}

interface Props {
  item: TestimonialItem;
  index: number;
  blockId: string;
  border: BlockBorderFields;
  look: TestimonialLook;
  roles: BlockTextRoles;
  varCtx?: VariableContext;
}

export function TestimonialCard({
  item,
  index: i,
  blockId,
  border,
  look,
  roles,
  varCtx,
}: Props) {
  const src = testimonialPhotoSrc(item.photoUrl);
  const [failedSrc, setFailedSrc] = useState<string | undefined>();
  const photoSrc = src !== undefined && src !== failedSrc ? src : undefined;
  const badge = testimonialBadgeText(item.badge);
  const layout = photoSrc ? look.layout : undefined;

  const photo = (style: CSSProperties) => (
    <img
      data-testid={`testimonial-photo-${i}-${blockId}`}
      src={photoSrc}
      alt=""
      loading="lazy"
      onError={() => setFailedSrc(photoSrc)}
      style={{ objectFit: 'cover', display: 'block', ...style }}
    />
  );

  const quote = look.quoteMarks ? '"' : null;
  const text = (
    <p
      data-testid={`testimonial-text-${i}-${blockId}`}
      style={{
        fontSize: 15,
        fontStyle: look.italic ? 'italic' : 'normal',
        color: 'var(--color-text-secondary, #434343)',
        marginBottom: 12,
        marginTop: 0,
        ...roles.secondary,
      }}
    >
      {quote}
      <RichTextContent value={asRichField(item.text)} varCtx={varCtx} />
      {quote}
    </p>
  );

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {layout === 'avatar' &&
        photo({
          width: TESTIMONIAL_AVATAR_SIZE,
          height: TESTIMONIAL_AVATAR_SIZE,
          borderRadius: '50%',
          flexShrink: 0,
        })}
      {/* WEB-809: author name is rich too (was a bare string). */}
      {/* WEB-1001: цвет — тем же механизмом, что у цитаты (инлайн
          через переменную): без него имя наследовало тёмный цвет
          фрагмента мимо вуального пина WEB-872 — чёрное на чёрном
          (живой замер, воронка onnn). Фолбэк одноуровневый — jsdom
          дропает вложенный var() в инлайн-color (шрам WEB-925). */}
      <span
        data-testid={`testimonial-name-${i}-${blockId}`}
        style={{
          fontWeight: 600,
          fontSize: 14,
          color: 'var(--color-text-primary, #262626)',
          ...roles.title,
        }}
      >
        <RichTextContent value={asRichField(item.name)} varCtx={varCtx} />
      </span>
      {item.rating !== undefined && (
        <span
          data-testid={`testimonial-stars-${i}-${blockId}`}
          style={{ color: RATING_STAR_COLOR, fontSize: 14 }}
        >
          {'★'.repeat(Math.round(item.rating))}
        </span>
      )}
      {badge !== undefined && (
        <span
          data-testid={`testimonial-badge-${i}-${blockId}`}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: look.badgeColor,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <ShieldCheckIcon />
          {badge}
        </span>
      )}
    </div>
  );

  const cardStyle: CSSProperties = {
    // WEB-1825: рамка снята с зашитых чисел на справочник
    // (blockBorder.ts) — три настройки автора действуют на
    // КАЖДУЮ карточку отзыва разом, потому что рамка тут одна и
    // та же, а поля живут на блоке, а не на отзыве. Пусто =
    // прежний вид, значения умолчания списаны с этой ветки.
    ...resolveBlockBorder(border),
    ...(look.cardBackground ? { background: look.cardBackground } : {}),
  };
  const cardTestId = `testimonial-card-${i}-${blockId}`;

  if (layout === 'left' || layout === 'top') {
    const isLeft = layout === 'left';
    return (
      <div
        data-testid={cardTestId}
        style={{
          ...cardStyle,
          padding: 0,
          marginBottom: 12,
          overflow: 'hidden',
          ...(isLeft ? { display: 'flex' } : {}),
        }}
      >
        {isLeft
          ? photo({
              width: TESTIMONIAL_LEFT_PHOTO_WIDTH,
              flexShrink: 0,
              alignSelf: 'stretch',
            })
          : photo({ width: '100%', height: TESTIMONIAL_TOP_PHOTO_HEIGHT })}
        <div
          style={{
            padding: CARD_PADDING,
            ...(isLeft ? { flex: 1, minWidth: 0 } : {}),
          }}
        >
          {text}
          {footer}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={cardTestId}
      style={{ ...cardStyle, padding: CARD_PADDING, marginBottom: 12 }}
    >
      {text}
      {footer}
    </div>
  );
}
